import { useState } from 'react';
import { salvarCenarioSimuladorSchema, type CenarioSimuladorResponse, type ConteudoCenarioDto } from '@triade/contracts';
import { useAtualizarCenarioSimulador, useSalvarCenarioSimulador } from '@/api/cenarios-simulador';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mensagemCenario } from './cenarios-salvos';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CAMPO = 'w-full rounded-[10px] border border-input bg-card px-3 text-body outline-none focus:border-primary focus:shadow-focus';

export interface CenarioAberto {
  id: string;
  nome: string;
  anotacoes: string | null;
}

export interface SalvarCenariosDialogProps {
  /** Estado atual da mesa (o formulario), no formato do contrato. */
  conteudo: ConteudoCenarioDto;
  /** Cenario aberto (se houver): "Salvar" regrava por PATCH; "Salvar como novo" cria por POST. */
  aberto: CenarioAberto | null;
  onSalvo: (salvo: CenarioSimuladorResponse) => void;
  onFechar: () => void;
}

/** Diálogo "Salvar cenários…": pede nome e anotacoes e grava a mesa atual. Nao calcula nada: envia o estado digitado. */
export function SalvarCenariosDialog({ conteudo, aberto, onSalvo, onFechar }: SalvarCenariosDialogProps) {
  const [nome, setNome] = useState(aberto?.nome ?? '');
  const [anotacoes, setAnotacoes] = useState(aberto?.anotacoes ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const criar = useSalvarCenarioSimulador();
  const atualizar = useAtualizarCenarioSimulador();
  const ocupado = criar.isPending || atualizar.isPending;

  async function gravar(como: 'regravar' | 'novo') {
    const nomeValido = salvarCenarioSimuladorSchema.shape.nome.safeParse(nome);
    if (!nomeValido.success) {
      setErro(nomeValido.error.issues[0]?.message ?? 'Nome inválido');
      return;
    }
    setErro(null);
    const notas = anotacoes.trim() || null;
    try {
      if (como === 'regravar' && aberto) {
        onSalvo(await atualizar.mutateAsync({ id: aberto.id, nome: nomeValido.data, anotacoes: notas, conteudo }));
      } else {
        onSalvo(await criar.mutateAsync({ nome: nomeValido.data, anotacoes: notas, conteudo }));
      }
    } catch (e) {
      setErro(mensagemCenario(e));
    }
  }

  return (
    <Dialog open onOpenChange={(a) => !a && onFechar()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void gravar(aberto ? 'regravar' : 'novo');
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Salvar cenários</DialogTitle>
            <DialogDescription>Guarda a mesa como está (produto, tabela, condição, data, praticado e cenários) para reabrir depois.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="salvar-cenarios-nome" className={ROTULO}>Nome</Label>
            <Input id="salvar-cenarios-nome" maxLength={80} value={nome} onChange={(e) => setNome(e.target.value)} aria-invalid={erro ? true : undefined} />
          </div>
          <div>
            <Label htmlFor="salvar-cenarios-anotacoes" className={ROTULO}>Anotações</Label>
            <textarea
              id="salvar-cenarios-anotacoes"
              value={anotacoes}
              onChange={(e) => setAnotacoes(e.target.value)}
              maxLength={2000}
              rows={4}
              className={`${CAMPO} py-2`}
            />
          </div>
          {erro && <p role="alert" className="text-caption text-danger-ink">{erro}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onFechar}>Cancelar</Button>
            {aberto && (
              <Button type="button" variant="outline" disabled={ocupado} onClick={() => void gravar('novo')}>
                Salvar como novo
              </Button>
            )}
            <Button type="submit" disabled={ocupado}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
